import {
  Header,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderName,
  HeaderPanel,
  OverflowMenu,
  OverflowMenuItem,
  SkipToContent,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
} from '@carbon/react';
import {
  Search,
  UserAvatar,
  Switcher as SwitcherIcon,
  Notification, // <--- Add this import
} from '@carbon/icons-react';
import { Icon } from './ui/icon.tsx';
import { ThemeSwitcherAction } from './theme-switcher-action.tsx';
import {
  useOptionalUser,
  userHasPermission,
  getUserDisplayName,
} from '../utils/user.ts';
import { Link, useNavigate, useRouteLoaderData } from 'react-router';
import { cn, getUserImgSrc } from '#app/utils/misc.tsx';
import { useState, useEffect, useRef } from 'react';
import { type loader as rootLoader } from '../root.tsx';
import NotificationsDrawer from './ui/notifications-drawer.tsx';
import InboxDrawer from './ui/inbox-drawer.tsx';
import { VirtualOfficeButton } from './virtual-office-button.tsx';
import { CalendarModalButton } from './calendar-modal-button.tsx';
import { BroadcastDispatcherModal } from './broadcast-dispatcher-modal.tsx';

interface CarbonHeaderProps {
  userPreference?: 'light' | 'dark' | 'system' | null;
  showMenuButton?: boolean;
  menuButton?: React.ReactNode;
  section?: string;
  showCalendarButton?: boolean;
  showBellButton?: boolean;
}

export function CarbonHeader({
  userPreference,
  showMenuButton = false,
  menuButton,
  section,
  showCalendarButton = false,
  showBellButton = false,
}: CarbonHeaderProps) {
  const user = useOptionalUser();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const systemName = rootData?.systemName ?? 'Bahandi';
  const notifications = rootData?.notifications ?? [];
  const unreadCount = rootData?.unreadNotificationCount ?? 0;

  const notificationPanelRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  const canAccessAdmin =
    user?.roles?.some((role) => role.name === 'admin') ?? false;
  const canAccessOps =
    user?.roles?.some(
      (role) => role.name === 'admin' || role.name === 'hr_manager',
    ) ?? false;
  const hasAnyApp = canAccessAdmin || canAccessOps;

  const hasUnread = unreadCount > 0;

  // Close notification panel when clicking outside
  useEffect(() => {
    if (!notificationsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is inside notification panel or button
      if (
        notificationPanelRef.current?.contains(target) ||
        notificationButtonRef.current?.contains(target)
      ) {
        return;
      }

      // Defer closing to next frame to allow other click handlers to process first
      requestAnimationFrame(() => {
        setNotificationsOpen(false);
      });
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [notificationsOpen]);

  return (
    <>
      <Header aria-label={systemName}>
        <SkipToContent />

        <HeaderName
          as={Link}
          to="/"
          prefix=""
          className="focus:border-0! text-white!"
        >
          <div className="inline text-white">{systemName}</div>
        </HeaderName>

        <HeaderGlobalBar>
          <ThemeSwitcherAction userPreference={userPreference} />

          {/* Virtual Office Button - Shows online workers with pulsing effect */}
          {user && <VirtualOfficeButton />}

          {/* Calendar Button - Shows calendar modal */}
          {user && showCalendarButton && <CalendarModalButton />}

          {/* Broadcast Dispatcher - Only visible to admins and HR managers */}
          {user && canAccessOps && <BroadcastDispatcherModal />}

          {/* =========================================
              THE NOTIFICATION BELL
              ========================================= */}
          {user && showBellButton && (
            <HeaderGlobalAction
              ref={notificationButtonRef}
              aria-label="Notifications"
              tooltipAlignment="end"
              isActive={notificationsOpen}
              onClick={() => {
                setNotificationsOpen((o) => !o);
              }}
              className="relative"
            >
              <Notification
                size={20}
                className="text-white/70! hover:text-white!"
              />
              {hasUnread && (
                <span className="absolute top-2 right-2.5 h-2 w-2 rounded-full bg-primary-base border border-gray-900" />
              )}
            </HeaderGlobalAction>
          )}

          {/* {user && hasAnyApp && (
            <HeaderGlobalAction
              aria-label="App Switcher"
              tooltipAlignment="end"
              isActive={switcherOpen}
              onClick={() => setSwitcherOpen((o) => !o)}
            >
              <SwitcherIcon size={20} />
            </HeaderGlobalAction>
          )} */}

          {user && (
            <OverflowMenu
              renderIcon={() => {
                if (user?.image?.objectKey) {
                  const userImageSrc = getUserImgSrc(user.image.objectKey);
                  return (
                    <img
                      src={userImageSrc}
                      alt="Profile"
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  );
                }
                return <UserAvatar size={20} />;
              }}
              size="lg"
              flipped
            >
              <OverflowMenuItem
                itemText={
                  <div className="text-left">
                    <div className="font-medium">
                      {getUserDisplayName(user)}
                    </div>
                    <div className="text-xs text-gray-400">
                      @{user.username}
                    </div>
                  </div>
                }
                disabled
              />
              <OverflowMenuItem itemText="My Desk" href="/me" />
              {/* <OverflowMenuItem itemText="Profile" href="/settings/profile" /> */}
              {canAccessOps && (
                <OverflowMenuItem itemText="Ops" hasDivider href="/ops" />
              )}
              {canAccessAdmin && (
                <OverflowMenuItem itemText="Admin" href="/admin/users" />
              )}
              <OverflowMenuItem
                itemText="Logout"
                hasDivider
                onClick={() => {
                  const form = document.createElement('form');
                  form.method = 'POST';
                  form.action = '/logout';
                  document.body.appendChild(form);
                  form.submit();
                }}
              />
            </OverflowMenu>
          )}
        </HeaderGlobalBar>

        {/* App Switcher Panel */}
        {user && hasAnyApp && (
          <HeaderPanel expanded={switcherOpen} aria-label="App Switcher">
            <Switcher aria-label="App Switcher" expanded={switcherOpen}>
              <SwitcherItem
                aria-labelledby="dashboard-switcher-item"
                onClick={() => {
                  navigate('/');
                  setSwitcherOpen(false);
                }}
              >
                Dashboard
              </SwitcherItem>
              {canAccessAdmin && (
                <>
                  <SwitcherDivider />
                  <SwitcherItem
                    aria-labelledby="admin-switcher-item"
                    onClick={() => {
                      navigate('/admin/users');
                      setSwitcherOpen(false);
                    }}
                  >
                    Admin
                  </SwitcherItem>
                </>
              )}
              {canAccessOps && (
                <>
                  <SwitcherDivider />
                  <SwitcherItem
                    aria-labelledby="ops-switcher-item"
                    onClick={() => {
                      navigate('/ops');
                      setSwitcherOpen(false);
                    }}
                  >
                    Ops
                  </SwitcherItem>
                </>
              )}
            </Switcher>
          </HeaderPanel>
        )}

        {/* Notifications Panel */}
        {user && showBellButton && (
          <HeaderPanel
            ref={notificationPanelRef}
            expanded={notificationsOpen}
            aria-label="Notifications"
            className="notifications-panel"
          >
            {notificationsOpen && (
              <InboxDrawer
                isOpen={notificationsOpen}
                onClose={() => setNotificationsOpen(false)}
                userId={user.id}
                notifications={notifications}
                unreadCount={unreadCount}
                isAdminOrHR={canAccessOps}
              />
            )}
          </HeaderPanel>
        )}
      </Header>
    </>
  );
}
